import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { readFeatureFlagFromCookieHeader, readFeatureFlagFromDocumentCookie } from '../feature-flags/read-feature-flag';

/**
 * TEC-482: the single flag that switches a browser session between the old location-filtering
 * experience (the current Finland/Remote grouped city list) and the new one (structured
 * hierarchical/recruiting-region filtering via regionTags). The backend has no equivalent
 * on/off switch, it always computes and stores both old and new location data for every job
 * (see lambda/enrichJob.ts), so which experience a viewer sees is entirely a client-side,
 * no-redeploy-needed decision. Managed from the `/backoffice/experiments` page (see
 * utils/feature-flags/feature-flag-registry.ts), on the shared cookie-flag mechanism every
 * flag there uses.
 *
 * 2026-09-10: flipped to default ON now that the job/company `regionTags` backfills, the
 * picker fixes, and the best-companies "by region" links have all been verified live (see
 * `web-app/documentation/LOCATION_SYSTEM_ROLLOUT.md` Stage 5). An explicit cookie value still
 * always wins over this default (see `read-feature-flag.ts`), so a session that dogfooded the
 * flag to `false` stays on the old experience; `/backoffice/experiments` remains the kill
 * switch for anyone who needs to opt back out.
 *
 * Starts true to match SSR, then picks up the cookie post-hydrate, so there's no flash of the
 * old experience for viewers who haven't explicitly opted out and no SSR/client markup mismatch.
 */
export const NEW_LOCATION_SYSTEM_COOKIE = 'dev_enable_new_location_system';

/** Exported for unit testing the cookie-parsing logic directly, without rendering the hook. */
export function readNewLocationSystemOptIn(): boolean {
  return readFeatureFlagFromDocumentCookie(NEW_LOCATION_SYSTEM_COOKIE, true);
}

/**
 * SSR-side reader, for the handful of `+data.ts` loaders (e.g. pages/index/+data.ts's map-mode
 * default bounds) that need this flag server-side rather than through the client-only hook below.
 * Takes a raw `Cookie` header string (however the caller already extracted it; this file has no
 * opinion on `pageContext.headersOriginal` vs. other Vike cookie-access shapes) rather than reading
 * `document.cookie` directly, since `document` doesn't exist during SSR.
 */
export function readNewLocationSystemOptInFromCookieHeader(cookieHeader: string | null | undefined): boolean {
  return readFeatureFlagFromCookieHeader(cookieHeader, NEW_LOCATION_SYSTEM_COOKIE, true);
}

export function useNewLocationSystemEnabled(): boolean {
  return useFeatureFlag(NEW_LOCATION_SYSTEM_COOKIE, true);
}
